/**
 * Full-length portraits for the character-select carousel.
 *
 * Its OWN scene and lights rather than the title world's: the roster runs while
 * the world behind the overlay is still rendering its own day/night lighting,
 * and a portrait must read the same at 3 a.m. on a storm world as at noon.
 * That also removes any need for the layer-mask juggling `livePortraitInto`
 * (regionNpcLife) does to isolate one body inside the live scene.
 *
 * Rendering is ROUND-ROBIN — one card per frame, into a cached render target
 * that is blitted to that card's 2D canvas. Two reasons:
 *   • cost. Five skinned bodies posed and drawn every frame, to fill five
 *     thumbnails, is most of a frame's budget for no perceptible gain.
 *   • correctness. `setStyle` streams clothing meshes in asynchronously, so a
 *     one-shot snapshot can freeze a half-dressed body. Re-rendering forever is
 *     self-healing by construction.
 */
import * as THREE from "three";
import type { CharacterStyle } from "@hyperlight/shared";
import { Avatar } from "../character/avatar";

/** Render resolution — a 1:2 standing frame. 256 wide covers a 2× DPR card
 *  (probes pin the width; only the height changed in the full-body rework). */
const W = 256;
const H = 512;
/** Metres between neighbours in the portrait scene. The lens stands 4.6 m from
 *  its subject with a 24° cone, so a neighbour 8 m off-axis never enters frame
 *  (and the far plane clips anything beyond 9 m regardless). */
const SPACING = 8;
/** Vertical framing: the whole body (feet at 0, hair to ~1.78 m) with a small
 *  margin — centre the 24° cone on mid-body and stand back far enough that the
 *  frame spans ~1.95 m. */
const LOOK_Y = 0.87;
const EYE_Y = 1.12;
const EYE_DIST = 4.6;

interface Entry {
  avatar: Avatar;
  context: CanvasRenderingContext2D;
  /** Portrait-scene position of this body. */
  x: number;
  /** Animation clock at this entry's last turn, so its dt stays truthful
   *  despite only being posed every Nth frame. */
  lastT: number;
  /** The suit this entry was asked for — probes compare it against what the
   *  materials actually ended up carrying. */
  suit: number;
  /** True until restyle() confirms the entry's REAL look — held entries are
   *  never painted, so a card cannot flash the bare creation style. */
  holdPaint: boolean;
}

export class CharacterPortraits {
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(24, W / H, 0.05, 9);
  private readonly entries: Entry[] = [];
  private target: THREE.WebGLRenderTarget | null = null;
  private pixels: Uint8Array | null = null;
  private image: ImageData | null = null;
  private cursor = 0;
  private t = 0;
  private readonly head = new THREE.Vector3();
  private readonly eye = new THREE.Vector3();

  constructor() {
    // A neutral three-point rig: warm key from camera-left, cool rim behind to
    // separate the silhouette from the card, and a soft hemisphere fill so the
    // underside of the jaw never crushes to black.
    const key = new THREE.DirectionalLight(0xffecd7, 3.2);
    key.position.set(2.5, 3.5, 4);
    const rim = new THREE.DirectionalLight(0x73b9ff, 2.4);
    rim.position.set(-3, 2, -4);
    this.scene.add(key, rim, new THREE.HemisphereLight(0xc5d8ff, 0x10131d, 1.7));
  }

  /** Swap one card's look in place — the roster shows `record.style` immediately
   *  and upgrades to the character's ACTUAL look (worn armor + transmog) when the
   *  profile prefetch lands. Keyed by the card's canvas, so a roster rebuild
   *  racing the fetch is inert (the old canvas is no longer an entry). */
  restyle(canvas: HTMLCanvasElement, style: CharacterStyle): Promise<void> {
    const e = this.entries.find((entry) => entry.context.canvas === canvas);
    if (!e) return Promise.resolve();
    e.avatar.setStyle(style);
    e.suit = style.suit;
    // Paint only once the pieces are mounted — the first blit shows the real
    // dressed look, never a bare/half-dressed intermediate.
    return e.avatar.styleSettled().then(() => { e.holdPaint = false; });
  }

  /** Rebuild the whole set — one body per card, in roster order. */
  build(cards: { style: CharacterStyle; canvas: HTMLCanvasElement }[]): void {
    this.clear();
    cards.forEach((card, index) => {
      const context = card.canvas.getContext("2d");
      if (!context) return;
      card.canvas.width = W;
      card.canvas.height = H;
      // NOT `sharedMaterials`: that path is for ambient citizens and makes
      // setStyle SKIP suit and accent (they live on the shared instances), so
      // every pilot in the line-up would wear the same colours — which is the
      // one thing these portraits exist to distinguish. Five material sets is
      // a rounding error next to getting the wrong pilot's suit.
      const avatar = new Avatar(this.scene, { crystalWings: true });
      // Avatars are constructed HIDDEN (avatar.ts) and every owner reveals its
      // own — without this the portrait scene renders empty and every card
      // comes out blank.
      avatar.group.visible = true;
      // No planet in here, so the foot IK has no floor to solve against and the
      // soles stay pointed (the creator mannequin's precedent).
      avatar.standOnFlatGround();
      avatar.group.position.set(index * SPACING, 0, 0);
      avatar.setStyle(card.style);
      this.entries.push({
        avatar, context, x: index * SPACING, lastT: this.t, suit: card.style.suit,
        holdPaint: true, // painted only after restyle() confirms the real look
      });
    });
  }

  /** Pose and draw ONE portrait. Call once per frame while the roster is up. */
  update(dt: number, renderer: THREE.WebGLRenderer): void {
    if (this.entries.length === 0) return;
    this.t += dt;
    const entry = this.entries[this.cursor % this.entries.length];
    this.cursor++;
    if (entry.holdPaint) return; // look not confirmed yet — never paint bare
    entry.avatar.update(Math.min(0.25, this.t - entry.lastT), 0, true, false);
    entry.lastT = this.t;

    const target = (this.target ??= (() => {
      const rt = new THREE.WebGLRenderTarget(W, H);
      // `renderer.outputColorSpace` only applies to the DEFAULT framebuffer; a
      // render target takes its own texture's space. Left linear, the readback
      // is linear light that putImageData then treats as sRGB — every body
      // comes out dark and mahogany-tinted (measured: tan skin 0xe0b48c landing
      // near 0x641805). Tagging the texture sRGB makes the renderer convert on
      // write, so the blit matches what the same body looks like on screen.
      rt.texture.colorSpace = THREE.SRGBColorSpace;
      return rt;
    })());
    const pixels = (this.pixels ??= new Uint8Array(W * H * 4));
    const image = (this.image ??= entry.context.createImageData(W, H));

    // Frame from in FRONT of the body, whatever its yaw: the rig's forward is
    // -Z, so the lens rides that axis instead of a fixed world direction.
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(entry.avatar.group.quaternion);
    this.head.set(entry.x, LOOK_Y, 0);
    // Full-length framing: stand back far enough for the whole body, eye a
    // little above mid-height for a gentle hero angle.
    this.eye.copy(this.head).addScaledVector(forward, EYE_DIST).setY(EYE_Y);
    this.camera.position.copy(this.eye);
    this.camera.up.set(0, 1, 0);
    this.camera.lookAt(this.head.x, LOOK_Y, this.head.z);

    const previousTarget = renderer.getRenderTarget();
    const previousClear = new THREE.Color();
    renderer.getClearColor(previousClear);
    const previousAlpha = renderer.getClearAlpha();
    renderer.setRenderTarget(target);
    // Transparent plate: the card's own background shows through, so the
    // portrait reads as part of the row rather than a pasted-on tile.
    renderer.setClearColor(0x000000, 0);
    renderer.clear();
    renderer.render(this.scene, this.camera);
    renderer.readRenderTargetPixels(target, 0, 0, W, H, pixels);
    renderer.setRenderTarget(previousTarget);
    renderer.setClearColor(previousClear, previousAlpha);

    // GL reads bottom-up; the 2D canvas is top-down.
    for (let y = 0; y < H; y++) {
      image.data.set(pixels.subarray((H - 1 - y) * W * 4, (H - y) * W * 4), y * W * 4);
    }
    entry.context.putImageData(image, 0, 0);
  }

  /** DEV/probe: what each portrait body is actually made of — the requested
   *  suit next to every distinct material colour hanging off its meshes. */
  debug(): unknown[] {
    const round = (v: number) => Math.round(v * 100) / 100;
    return this.entries.map((entry) => {
      const meshes: unknown[] = [];
      const box = new THREE.Box3();
      entry.avatar.group.traverse((object) => {
        const material = (object as THREE.Mesh).material;
        if (!material) return;
        const one = Array.isArray(material) ? material[0] : material;
        const color = (one as THREE.MeshLambertMaterial).color;
        const bounds = new THREE.Box3().setFromObject(object);
        meshes.push({
          name: object.name || object.type,
          visible: object.visible,
          color: color ? `#${color.getHexString()}` : null,
          min: bounds.isEmpty() ? null : [round(bounds.min.x), round(bounds.min.y), round(bounds.min.z)],
          max: bounds.isEmpty() ? null : [round(bounds.max.x), round(bounds.max.y), round(bounds.max.z)],
        });
      });
      box.setFromObject(entry.avatar.group);
      return {
        x: entry.x,
        visible: entry.avatar.group.visible,
        suit: `#${entry.suit.toString(16).padStart(6, "0")}`,
        groupPos: [round(entry.avatar.group.position.x), round(entry.avatar.group.position.y), round(entry.avatar.group.position.z)],
        bounds: box.isEmpty() ? null : {
          min: [round(box.min.x), round(box.min.y), round(box.min.z)],
          max: [round(box.max.x), round(box.max.y), round(box.max.z)],
        },
        camera: {
          pos: [round(this.camera.position.x), round(this.camera.position.y), round(this.camera.position.z)],
          headTarget: [round(entry.x), LOOK_Y, 0],
        },
        meshes,
      };
    });
  }

  /** Drop every body (leaves the render target cached for the next roster). */
  clear(): void {
    for (const entry of this.entries) entry.avatar.dispose(this.scene);
    this.entries.length = 0;
    this.cursor = 0;
  }

  dispose(): void {
    this.clear();
    this.target?.dispose();
    this.target = null;
    this.pixels = null;
    this.image = null;
  }
}
